import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Table, Space, Modal, message, Tag, Avatar, Input, Select, Layout, Dropdown, Grid, Drawer, Badge, Empty, Spin } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  CrownOutlined,
  SearchOutlined,
  FilterOutlined,
  BarChartOutlined,
  LinkOutlined,
  HeartOutlined,
  AppstoreOutlined,
  MoreOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  RightOutlined,
  ShareAltOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../shared/DateField';
import { formatDualDate } from '../../utils/eventDate';
import AppContext from '../../AppContext';
import { showFeatureNotIncludedModal } from '../../utils/featureGate';
import axios from 'axios';
// import { BASE_URL, LANDING_PAGE_URL } from '../../Constants';
import { BASE_URL, LANDING_PAGE_URL } from '../../Constants';


const { Search } = Input;
const { Option } = Select;
const { Content } = Layout;

const EventManagementPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir?.() === 'rtl';
  // In the Arabic UI show the event's Arabic title/location (falling back to the English
  // one when no Arabic was entered), and format dates in the page language.
  const isArabicUi = (i18n.language || '').startsWith('ar');
  const eventTitle = (record) => (isArabicUi && record.arabicTitle?.trim()) || record.title;
  const eventLocation = (record) => (isArabicUi && record.arabicLocation?.trim()) || record.location;
  const eventDate = (value, record) =>
    value ? formatDate(String(value).slice(0, 10), i18n.language, record?.calendarPreference, record?.hijriAdjustmentDays || 0) : '';
  const { profile, primaryColor } = useContext(AppContext);
  // `=== false` so the first render (before antd reports breakpoints) defaults to desktop.
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  // Org-wide event pool (parent company + sub-companies), from GET /event/pool-usage.
  // Kept separate from `totalRecords` (which is just this filtered table's row count).
  const [pool, setPool] = useState({ used: 0, max: 0, remaining: 0, hasEventAccess: false });
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [dateRange, setDateRange] = useState({
    startDateFrom: null,
    startDateTo: null,
  });
  const [isTypeModalVisible, setIsTypeModalVisible] = useState(false);
  // This company's active purchased entitlements (full records, not just product types), used
  // both for the event-type picker below and to show each wedding event's real invitation limit
  // (maxInvitations) instead of its hardcoded Event.Capacity. Empty when there's no entitlement
  // data at all (e.g. a legacy/non-virtual company with generic event access).
  const [activeEntitlements, setActiveEntitlements] = useState([]);
  const entitlementProductTypes = Array.from(
    new Set(activeEntitlements.map((e) => e.productType).filter(Boolean))
  );

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        companyId: profile.companyId,
        take: pagination.pageSize.toString(),
        skip: ((pagination.current - 1) * pagination.pageSize).toString(),
      });

      // Add optional filters
      if (searchText) {
        params.append('searchText', searchText);
      }

      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      if (dateRange.startDateFrom) {
        params.append('startDateFrom', dateRange.startDateFrom);
      }

      if (dateRange.startDateTo) {
        params.append('startDateTo', dateRange.startDateTo);
      }

      const response = await axios.get(`${BASE_URL}/event?${params.toString()}`);

      setEvents(response.data.items || []);
      setTotalRecords(response.data.totalRecords || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      message.error(t('eventManagement.errors.loadFailed'));
      setEvents([]);
      setTotalRecords(0);
      setLoading(false);
    }
  }, [profile?.companyId, pagination.pageSize, pagination.current, searchText, statusFilter, dateRange.startDateFrom, dateRange.startDateTo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Pool usage depends only on the company, not on search/filter/pagination, so it is
  // fetched on mount and refreshed explicitly after a create/delete.
  const fetchPoolUsage = useCallback(async () => {
    if (!profile?.companyId) return;
    try {
      const { data } = await axios.get(
        `${BASE_URL}/event/pool-usage?companyId=${profile.companyId}`
      );
      setPool({
        used: data.used || 0,
        max: data.max || 0,
        remaining: data.remaining ?? 0,
        hasEventAccess: !!data.hasEventAccess,
      });
    } catch (error) {
      console.error('Error fetching event pool usage:', error);
    }
  }, [profile?.companyId]);

  useEffect(() => {
    fetchPoolUsage();
  }, [fetchPoolUsage]);

  // Only relevant for virtual (Individual Event) companies, which purchase a wedding or event
  // package as an entitlement; a normal company with generic hasEventAccess has none of these,
  // so the request harmlessly returns an empty list and the type picker keeps both options.
  const fetchActiveEntitlements = useCallback(async () => {
    if (!profile?.companyId) return;
    try {
      const { data } = await axios.get(`${BASE_URL}/Company/my-entitlements`);
      setActiveEntitlements((data || []).filter((e) => e.status === 'active'));
    } catch (error) {
      console.error('Error fetching entitlements:', error);
    }
  }, [profile?.companyId]);

  useEffect(() => {
    fetchActiveEntitlements();
  }, [fetchActiveEntitlements]);

  // A marriage event's real invitation limit is the backing entitlement's maxInvitations, not
  // Event.Capacity (which the backend currently sets to a fixed placeholder for wedding events).
  // Matched by the event's own entitlementId so this is correct even with multiple active
  // entitlements; falls back to the event's own capacity if no matching entitlement is found.
  const getRegistrationLimit = (record) => {
    if (record.eventType === 'marriage') {
      const entitlement = activeEntitlements.find((e) => e.id === record.entitlementId);
      if (entitlement && entitlement.maxInvitations > 0) {
        return entitlement.maxInvitations;
      }
    }
    return record.capacity;
  };

  // When the company purchased only one product type (e.g. a Wedding package), there is only
  // one valid event type to create — skip the picker and go straight to the matching form.
  // Only show the picker when there's a genuine choice (both types purchased, or no
  // entitlement data at all, e.g. a legacy/non-virtual company with generic event access).
  const showGeneralEventOption = entitlementProductTypes.length === 0 || entitlementProductTypes.includes('event');
  const showMarriageEventOption = entitlementProductTypes.length === 0 || entitlementProductTypes.includes('wedding');

  const handleCreateEventClick = () => {
    if (entitlementProductTypes.length === 1) {
      const type = entitlementProductTypes[0] === 'wedding' ? 'marriage' : 'general';
      navigate(`/event-form?type=${type}`);
      return;
    }
    setIsTypeModalVisible(true);
  };

  const handleDeleteEvent = async (eventId) => {
    Modal.confirm({
      title: t('eventManagement.deleteConfirm.title'),
      content: t('eventManagement.deleteConfirm.content'),
      okText: t('eventManagement.deleteConfirm.okText'),
      okType: 'danger',
      cancelText: t('eventManagement.deleteConfirm.cancelText'),
      onOk: async () => {
        try {
          await axios.delete(`${BASE_URL}/event/${eventId}`);
          message.success(t('eventManagement.deleteSuccess'));
          fetchEvents(); // Refresh the list
          fetchPoolUsage(); // A delete frees a slot in the shared pool
        } catch (error) {
          console.error('Error deleting event:', error);
          message.error(t('eventManagement.errors.deleteFailed'));
        }
      },
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 }); // Reset to first page on search
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPagination({ ...pagination, current: 1 }); // Reset to first page on filter
  };

  const handleLandingLink = (landingPageSlug, eventTitle) => {
    if (!landingPageSlug) {
      message.error(t('eventManagement.errors.landingSlugMissing'));
      return;
    }
    // Generate subdomain from title (remove hash suffix from slug)
    // const titleSlug = eventTitle
    //   .toLowerCase()
    //   .replace(/[^a-z0-9]+/g, '-')
    //   .replace(/^-|-$/g, '');
    // const url = `http://${titleSlug}.uat-events.future-cards.com/events/${landingPageSlug}/`;
    // window.open(url, '_blank');
    
    const url = `${LANDING_PAGE_URL}/events/${landingPageSlug}`;
    window.open(url, '_blank');
  };

  // Copies the public event link so the host can paste it into WhatsApp or anywhere else.
  const handleShareLink = async (landingPageSlug) => {
    if (!landingPageSlug) {
      message.error(t('eventManagement.errors.landingSlugMissing'));
      return;
    }
    const url = `${LANDING_PAGE_URL}/events/${landingPageSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      message.success(t('eventManagement.actions.linkCopied'));
    } catch {
      window.open(url, '_blank');
    }
  };

  // Confirmed / waiting / declined at a glance, so the host does not have to open the event.
  const renderRsvpSummary = (record) => (
    <div className="text-xs mt-2" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <span style={{ color: '#52c41a' }}>{t('eventManagement.rsvp.confirmed', { count: record.acceptedRegistrations ?? 0 })}</span>
      <span style={{ color: '#fa8c16' }}>{t('eventManagement.rsvp.pending', { count: record.pendingRegistrations ?? 0 })}</span>
      <span style={{ color: '#ff4d4f' }}>{t('eventManagement.rsvp.declined', { count: record.rejectedRegistrations ?? 0 })}</span>
    </div>
  );

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      active: { color: 'green', key: 'active' },
      draft: { color: 'orange', key: 'draft' },
      completed: { color: 'blue', key: 'completed' },
      cancelled: { color: 'red', key: 'cancelled' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Tag color={config.color}>{t(`eventManagement.status.${config.key}`)}</Tag>;
  };

  const getEventTypeTag = (type) => {
    if (type === 'marriage') {
      return (
        <Tag color="#eb2f96" icon={<HeartOutlined />} style={{ borderRadius: '4px', fontWeight: 'bold' }}>
          {t('eventManagement.eventType.marriage')}
        </Tag>
      );
    }
    return (
      <Tag color="blue" icon={<AppstoreOutlined />} style={{ borderRadius: '4px' }}>
        {t('eventManagement.eventType.general')}
      </Tag>
    );
  };

  // Mirrors getRegistrationLimit's entitlement match (by the event's own entitlementId, falling
  // back to the company's other active entitlement) so this needs no extra per-row fetch.
  // activeEntitlements is empty for legacy/non-virtual companies, which are grandfathered in.
  const hasAnalyticsAccess = (record) => {
    if (activeEntitlements.length === 0) return true;
    const entitlement =
      activeEntitlements.find((e) => e.id === record.entitlementId) || activeEntitlements[0];
    return !!(entitlement?.hasLiveDashboard || entitlement?.hasPostEventAnalytics);
  };

  const getMoreItems = (record) => [
      ...(record.eventType !== 'marriage'
        ? [
            {
              key: 'analytics',
              icon: <BarChartOutlined />,
              label: t('eventManagement.actions.viewAnalytics'),
              onClick: () => {
                if (!hasAnalyticsAccess(record)) {
                  showFeatureNotIncludedModal(
                    t('eventManagement.actions.analyticsNotIncluded'),
                    t('common.featureNotIncludedTitle'),
                    t('common.ok')
                  );
                  return;
                }
                navigate(`/dashboard/events/${record.id}/analytics`);
              },
            },
          ]
        : []),
      {
        key: 'landing',
        icon: <LinkOutlined />,
        label: t('eventManagement.actions.goToLandingPage'),
        disabled: !record.landingPageSlug,
        onClick: () => handleLandingLink(record.landingPageSlug, record.title),
      },
      { type: 'divider' },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('eventManagement.actions.deleteEvent'),
        danger: true,
        onClick: () => handleDeleteEvent(record.id),
      },
    ];

  const columns = [
    {
      title: t('eventManagement.columns.event'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className="flex items-start gap-3">
          <Avatar
            shape="square"
            size={56}
            src={record.backgroundImage}
            icon={<CalendarOutlined />}
          />
          <div>
            <div className="font-semibold text-gray-900 text-[15px] flex items-center flex-wrap gap-2">
              {eventTitle(record)}
              {getEventTypeTag(record.eventType)}
            </div>
            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1 flex-wrap">
              <span>{eventDate(record.startDate, record)}</span>
              {eventLocation(record) && (
                <>
                  <span>·</span>
                  <EnvironmentOutlined />
                  <span>{eventLocation(record)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('eventManagement.columns.usage'),
      key: 'registrations',
      render: (_, record) => {
        const limit = getRegistrationLimit(record);
        const used = record.totalRegistrations ?? 0;
        const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
        const usageLabel = record.eventType === 'marriage' ? t('eventManagement.usage.invitations') : t('eventManagement.usage.registrations');
        return (
          <div style={{ minWidth: 150 }}>
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{usageLabel}</span>
              <span className="text-xs font-semibold" style={{ color: primaryColor }}>{pct}%</span>
            </div>
            <div className="text-lg font-bold leading-tight" style={{ color: primaryColor }}>
              {used} <span className="text-sm font-normal text-gray-400">/ {limit}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1.5">
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: primaryColor
                }}
              />
            </div>
            {record.eventType === 'marriage' ? (
              renderRsvpSummary(record)
            ) : (
              <div className="text-xs text-gray-500 mt-1.5 flex items-center">
                <CrownOutlined className="text-yellow-500 mr-1" />
                {t('eventManagement.usage.vip', { count: record.vipRegistrations ?? 0 })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('eventManagement.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: t('eventManagement.columns.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['md'],
      render: (date, record) => (
        <div>
          <div className="text-sm">{date ? formatDualDate(String(date).slice(0, 10), i18n.language, 'gregorian').primary : ''}</div>
          <div className="text-xs text-gray-500">{t('eventManagement.usage.createdBy', { name: record.createdBy })}</div>
        </div>
      ),
    },
    {
      title: t('eventManagement.columns.actions'),
      key: 'actions',
      width: 450,
      render: (_, record) => {
        const moreItems = getMoreItems(record);
        return (
          <Space size={6} wrap={false} style={{ whiteSpace: 'nowrap' }}>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/event-registrations/${record.id}`)}
              style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
            >
              {record.eventType === 'marriage' ? t('eventManagement.actions.manageGuests') : t('eventManagement.actions.viewRegistrations')}
            </Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/event-form/${record.id}`)}>
              {t('eventManagement.actions.edit')}
            </Button>
            <Button size="small" icon={<TeamOutlined />} onClick={() => navigate(`/event-organizers/${record.id}`)}>
              {t('eventManagement.actions.organizers')}
            </Button>
            <Button
              size="small"
              icon={<ShareAltOutlined />}
              disabled={!record.landingPageSlug}
              onClick={() => handleShareLink(record.landingPageSlug)}
              title={t('eventManagement.actions.shareLink')}
              aria-label={t('eventManagement.actions.shareLink')}
            />

            <Dropdown menu={{ items: moreItems }} trigger={['click']} placement="bottomRight">
              <Button size="small" icon={<MoreOutlined />} title={t('eventManagement.actions.moreActions')} aria-label={t('eventManagement.actions.moreActions')} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const limitReached = pool.used >= pool.max;
  const activeFilterCount = statusFilter !== 'all' ? 1 : 0;

  // Mobile replacement for the table: one tappable card per event.
  const renderEventCard = (record) => {
    const limit = getRegistrationLimit(record);
    const used = record.totalRegistrations ?? 0;
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const isMarriage = record.eventType === 'marriage';
    const usageLabel = isMarriage ? t('eventManagement.usage.invitations') : t('eventManagement.usage.registrations');
    return (
      <div
        key={record.id}
        onClick={() => navigate(`/event-registrations/${record.id}`)}
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 12,
          padding: 14,
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          cursor: 'pointer',
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar
            shape="square"
            size={48}
            src={record.backgroundImage}
            icon={<CalendarOutlined />}
            style={{ flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="font-semibold text-gray-900 text-[15px]"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {eventTitle(record)}
            </div>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              {getEventTypeTag(record.eventType)}
              {getStatusTag(record.status)}
            </div>
          </div>
          <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: 'registrations',
                    icon: <EyeOutlined />,
                    label: t('eventManagement.actions.viewRegistrations'),
                    onClick: () => navigate(`/event-registrations/${record.id}`),
                  },
                  {
                    key: 'edit',
                    icon: <EditOutlined />,
                    label: t('eventManagement.actions.editEvent'),
                    onClick: () => navigate(`/event-form/${record.id}`),
                  },
                  {
                    key: 'organizers',
                    icon: <TeamOutlined />,
                    label: t('eventManagement.actions.organizers'),
                    onClick: () => navigate(`/event-organizers/${record.id}`),
                  },
                  { type: 'divider' },
                  ...getMoreItems(record),
                ],
              }}
            >
              <Button type="text" size="large" icon={<MoreOutlined />} aria-label={t('eventManagement.actions.moreActions')} />
            </Dropdown>
          </div>
        </div>

        <div className="text-xs text-gray-500 mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span><CalendarOutlined className="me-1" />{eventDate(record.startDate, record)}</span>
          {eventLocation(record) && (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <EnvironmentOutlined className="me-1" />{eventLocation(record)}
            </span>
          )}
        </div>

        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f5f5f5' }}>
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{usageLabel}</span>
            <span className="text-base font-bold" style={{ color: primaryColor }}>
              {used} <span className="text-xs font-normal text-gray-400">/ {limit}</span>
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1.5">
            <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: primaryColor }} />
          </div>
          {!isMarriage && (
            <div className="text-xs text-gray-500 mt-2 flex items-center">
              <CrownOutlined className="text-yellow-500 me-1" />
              {t('eventManagement.usage.vip', { count: record.vipRegistrations ?? 0 })}
            </div>
          )}
          {isMarriage && renderRsvpSummary(record)}
        </div>

        <div className="mt-3" style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/event-registrations/${record.id}`)}
            style={{ flex: 2, backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            {isMarriage ? t('eventManagement.actions.manageGuests') : t('eventManagement.actions.viewRegistrations')}
          </Button>
          <Button icon={<EditOutlined />} onClick={() => navigate(`/event-form/${record.id}`)} style={{ flex: 1 }}>
            {t('eventManagement.actions.edit')}
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => navigate(`/event-organizers/${record.id}`)}
            title={t('eventManagement.actions.organizers')}
            aria-label={t('eventManagement.actions.organizers')}
          />
          <Button
            icon={<ShareAltOutlined />}
            disabled={!record.landingPageSlug}
            onClick={() => handleShareLink(record.landingPageSlug)}
            title={t('eventManagement.actions.shareLink')}
            aria-label={t('eventManagement.actions.shareLink')}
          />
        </div>
      </div>
    );
  };

  const renderMobileList = () => {
    const { current, pageSize } = pagination;
    const start = totalRecords === 0 ? 0 : (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, totalRecords);
    return (
      <Spin spinning={loading}>
        {events.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }}>
            {!loading && !limitReached && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateEventClick} style={{ backgroundColor: primaryColor, borderColor: primaryColor }}>
                {t('eventManagement.createEvent')}
              </Button>
            )}
          </Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {events.map(renderEventCard)}
          </div>
        )}
        {totalRecords > pageSize && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-gray-500">{start}–{end} / {totalRecords}</span>
            <div className="flex gap-2">
              <Button
                icon={isRtl ? <RightOutlined /> : <LeftOutlined />}
                disabled={current <= 1}
                onClick={() => setPagination({ ...pagination, current: current - 1 })}
              />
              <Button
                icon={isRtl ? <LeftOutlined /> : <RightOutlined />}
                disabled={end >= totalRecords}
                onClick={() => setPagination({ ...pagination, current: current + 1 })}
              />
            </div>
          </div>
        )}
      </Spin>
    );
  };

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {t('eventManagement.title')}
          </h1>
        </div>
      </header>
      <Content className="rounded-lg px-3 py-4 shadow bg-white sm:px-6 sm:py-6">
        <div className="bg-white">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4 sm:gap-6">
            {!isMobile && (
              <div>
                <p className="text-gray-600">{t('eventManagement.subtitle')}</p>
              </div>
            )}
            {pool.max > 0 && (
              <div
                style={{ minWidth: 200 }}
                className={isMobile ? 'w-full bg-gray-50 rounded-lg p-3' : 'w-full sm:w-auto'}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{t('eventManagement.pool.events')}</span>
                  <span className="text-[11px] text-gray-400">{t('eventManagement.pool.remaining', { count: pool.remaining })}</span>
                </div>
                <div className="text-base font-semibold text-gray-800">
                  {pool.used} <span className="text-gray-400 font-normal">/ {pool.max} {t('eventManagement.pool.used')}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: `${pool.max > 0 ? Math.min(100, (pool.used / pool.max) * 100) : 0}%`,
                      backgroundColor: primaryColor
                    }}
                  />
                </div>
              </div>
            )}
            {/* On mobile the create action is a floating button, except when the limit is reached */}
            {(!isMobile || limitReached) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateEventClick}
              size="large"
              block
              className="sm:w-auto"
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                color: '#fff',
                opacity: pool.used >= pool.max ? 0.4 : 1,
              }}
              disabled={pool.used >= pool.max}
              title={pool.used >= pool.max ? t('eventManagement.eventLimitReached') : ""}
            >
              {t('eventManagement.createEvent')}
            </Button>
            )}
            {isMobile && limitReached && (
              <p className="text-xs text-gray-500 -mt-2">{t('eventManagement.eventLimitReached')}</p>
            )}
          </div>

          <Modal
            title={t('eventManagement.typeModal.title')}
            open={isTypeModalVisible}
            onCancel={() => setIsTypeModalVisible(false)}
            footer={null}
            width={700}
            style={{ maxWidth: '90vw', top: 20 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {showGeneralEventOption && (
              <div
                className="group relative bg-white rounded-2xl p-5 sm:p-8 cursor-pointer transition-all duration-300 border border-gray-100 hover:border-transparent hover:shadow-xl hover:-translate-y-1"
                onClick={() => {
                  setIsTypeModalVisible(false);
                  navigate('/event-form?type=general');
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}
                >
                  <AppstoreOutlined style={{ fontSize: '32px' }} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{t('eventManagement.typeModal.generalTitle')}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t('eventManagement.typeModal.generalDescription')}
                </p>
                <div className="mt-4 flex items-center text-sm font-semibold" style={{ color: primaryColor }}>
                  {t('eventManagement.typeModal.generalCta')}
                </div>
              </div>
              )}

              {showMarriageEventOption && (
              <div
                className="group relative bg-white rounded-2xl p-5 sm:p-8 cursor-pointer transition-all duration-300 border border-gray-100 hover:border-transparent hover:shadow-xl hover:-translate-y-1"
                onClick={() => {
                  setIsTypeModalVisible(false);
                  navigate('/event-form?type=marriage');
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300 group-hover:scale-110"
                  style={{ backgroundColor: '#fff0f6', color: '#eb2f96' }}
                >
                  <HeartOutlined style={{ fontSize: '32px' }} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{t('eventManagement.typeModal.marriageTitle')}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t('eventManagement.typeModal.marriageDescription')}
                </p>
                <div className="mt-4 flex items-center text-sm font-semibold text-pink-600">
                  {t('eventManagement.typeModal.marriageCta')}
                </div>
              </div>
              )}
            </div>
          </Modal>

          {isMobile ? (
            <div className="mb-4">
              <div style={{ display: 'flex', gap: 8 }}>
                <Search
                  placeholder={t('eventManagement.search.placeholder')}
                  allowClear
                  size="large"
                  style={{ flex: 1, minWidth: 0 }}
                  onSearch={handleSearch}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      handleSearch('');
                    }
                  }}
                />
                <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
                  <Button
                    size="large"
                    icon={<FilterOutlined />}
                    onClick={() => setFilterDrawerOpen(true)}
                    aria-label={t('eventManagement.search.filterByStatus')}
                  />
                </Badge>
              </div>
              {statusFilter !== 'all' && (
                <div className="mt-2">
                  <Tag closable onClose={() => handleStatusChange('all')}>
                    {t(`eventManagement.status.${statusFilter}`)}
                  </Tag>
                </div>
              )}
            </div>
          ) : (
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center mb-6">
            <Search
              placeholder={t('eventManagement.search.placeholder')}
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              className="w-full sm:flex-1 sm:max-w-md"
              onSearch={handleSearch}
              onChange={(e) => {
                if (e.target.value === '') {
                  handleSearch('');
                }
              }}
            />
            <Select
              placeholder={t('eventManagement.search.filterByStatus')}
              size="large"
              className="w-full sm:w-48"
              value={statusFilter}
              onChange={handleStatusChange}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">{t('eventManagement.status.all')}</Option>
              <Option value="active">{t('eventManagement.status.active')}</Option>
              <Option value="draft">{t('eventManagement.status.draft')}</Option>
              <Option value="completed">{t('eventManagement.status.completed')}</Option>
              <Option value="cancelled">{t('eventManagement.status.cancelled')}</Option>
            </Select>
          </div>
          )}

          {isMobile ? (
            renderMobileList()
          ) : (
          <Table
            columns={columns.map((col) => ({ ...col, onCell: () => ({ style: { verticalAlign: 'middle' } }) }))}
            dataSource={events}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1000 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalRecords,
              showSizeChanger: true,
              showTotal: (total, range) =>
                t('eventManagement.tablePagination', { from: range[0], to: range[1], total }),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            className="shadow-sm"
          />
          )}

          {/* Mobile: floating create button */}
          {isMobile && !limitReached && (
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleCreateEventClick}
              aria-label={t('eventManagement.createEvent')}
              style={{
                position: 'fixed',
                insetInlineEnd: 20,
                bottom: 20,
                zIndex: 1000,
                width: 56,
                height: 56,
                fontSize: 22,
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              }}
            />
          )}

          {/* Mobile status filter bottom sheet */}
          <Drawer
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            placement="bottom"
            height="auto"
            title={t('eventManagement.search.filterByStatus')}
            footer={
              <Button type="primary" block size="large" onClick={() => setFilterDrawerOpen(false)}>
                {t('eventManagement.search.done')}
              </Button>
            }
          >
            <Select
              size="large"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={handleStatusChange}
            >
              <Option value="all">{t('eventManagement.status.all')}</Option>
              <Option value="active">{t('eventManagement.status.active')}</Option>
              <Option value="draft">{t('eventManagement.status.draft')}</Option>
              <Option value="completed">{t('eventManagement.status.completed')}</Option>
              <Option value="cancelled">{t('eventManagement.status.cancelled')}</Option>
            </Select>
          </Drawer>
        </div>
      </Content>
    </Layout>
  );
};

export default EventManagementPage;